/**
 * Drag Behavior
 *
 * Handles node dragging:
 * - Drag selected nodes
 * - Alt+drag to duplicate (with type preservation if callbacks provided)
 * - Escape to cancel
 */

import type { DragBehaviorContext, DuplicateNodeData } from './types';
import type { Point } from '../types';
import * as viewState from '../view-state';

/**
 * Register drag behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerDragBehavior(ctx: DragBehaviorContext): () => void {
	const {
		input,
		getViewState,
		updateViewState,
		hitTest,
		getNodes,
		getEdges,
		screenToWorld,
		moveNodes,
		duplicateNodes,
		getNodeData,
		createNodeWithData,
		createEdge,
		log,
	} = ctx;
	const unsubscribers: (() => void)[] = [];

	// Check if we have type-preserving duplication available
	const hasTypePreservingDuplication = !!(getNodeData && createNodeWithData);

	// =========================================================================
	// Start Drag (mousedown on node)
	// =========================================================================

	unsubscribers.push(
		input.on('mousedown', { mode: 'default' }, (inputCtx) => {
			// Only start drag on left mouse button
			if (inputCtx.button !== 0) return;

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const hit = hitTest(point);

			// Only start drag if we clicked a node
			if (!hit.nodeId) return;

			// Don't start drag if clicking on a connection handle or resize handle
			const state = getViewState();
			if (state.hovered.handleId) return;
			if (state.hovered.resizeHandleId) return;

			const copying = inputCtx.modifiers.alt;

			// Check if we toggled OFF a node (shift+click on selected = deselect)
			// In that case, don't start a drag
			const wasSelected = state.selection.has(hit.nodeId);
			const isToggleOff = inputCtx.modifiers.shift && wasSelected;
			if (isToggleOff) {
				// Selection behavior already toggled it off, don't drag
				return;
			}

			// Get nodes to drag (all selected nodes, but only actual nodes, not edges)
			const nodeIds = new Set(getNodes().map((n) => n.id));
			const selectedIds = viewState.getSelectedIds(state);
			const nodesToDrag = selectedIds.filter((id) => nodeIds.has(id));

			// If no nodes selected (edge selected?), nothing to drag
			if (nodesToDrag.length === 0) return;

			// Start drag
			updateViewState(viewState.startDrag(state, nodesToDrag, point, copying));
			input.pushMode('drag');
			log?.(
				'drag-start',
				`${nodesToDrag.length} nodes${copying ? ' (copying)' : ''}`,
			);
		}),
	);

	// =========================================================================
	// Drag Move
	// =========================================================================

	unsubscribers.push(
		input.on('mousemove', { mode: 'drag' }, (inputCtx) => {
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			updateViewState(viewState.updateDrag(getViewState(), point));
		}),
	);

	// =========================================================================
	// Drag End
	// =========================================================================

	/**
	 * Duplicate nodes with type preservation using getNodeData + createNodeWithData.
	 * Returns a promise that resolves to the new node IDs.
	 */
	async function duplicateNodesWithData(
		nodeIds: string[],
		offset: Point,
	): Promise<{ newIds: string[]; edgeCount: number }> {
		if (!getNodeData || !createNodeWithData) {
			// Fallback to legacy duplication (edges handled by duplicateNodes internally)
			return { newIds: duplicateNodes(nodeIds, offset), edgeCount: 0 };
		}

		const nodes = getNodes();
		const newIds: string[] = [];
		const idMap = new Map<string, string>();

		for (const nodeId of nodeIds) {
			const node = nodes.find((n) => n.id === nodeId);
			if (!node) continue;

			const nodeData = getNodeData(nodeId);
			if (!nodeData) {
				log?.('duplicate-skip', `No data for node ${nodeId}`);
				continue;
			}

			// Calculate the new position (top-left + drag offset)
			const newPosition: Point = {
				x: node.position.x + offset.x,
				y: node.position.y + offset.y,
			};

			// Create the new node (may be async for SDK persistence)
			const newId = await Promise.resolve(
				createNodeWithData(
					{
						type: nodeData.type,
						size: nodeData.size ?? node.size,
						rotation: nodeData.rotation ?? node.rotation,
						data: nodeData.data,
					},
					newPosition,
				),
			);
			newIds.push(newId);
			idMap.set(nodeId, newId);
		}

		// Duplicate edges between copied nodes
		let edgeCount = 0;
		if (createEdge && idMap.size > 1) {
			const draggedSet = new Set(nodeIds);
			for (const edge of getEdges()) {
				if (
					draggedSet.has(edge.sourceNodeId) &&
					draggedSet.has(edge.targetNodeId)
				) {
					const newSource = idMap.get(edge.sourceNodeId);
					const newTarget = idMap.get(edge.targetNodeId);
					if (newSource && newTarget) {
						createEdge(newSource, newTarget);
						edgeCount++;
					}
				}
			}
		}

		return { newIds, edgeCount };
	}

	unsubscribers.push(
		input.on('mouseup', { mode: 'drag' }, () => {
			const state = getViewState();
			const offset = viewState.getDragOffset(state);

			if (state.drag && offset) {
				if (state.drag.copying) {
					if (hasTypePreservingDuplication) {
						// Use type-preserving duplication (async)
						duplicateNodesWithData(state.drag.nodes, offset)
							.then(({ newIds, edgeCount }) => {
								const edgePart = edgeCount > 0 ? `, ${edgeCount} edge(s)` : '';
								log?.(
									'duplicate',
									`${newIds.length} nodes${edgePart} at offset (${offset.x.toFixed(0)}, ${offset.y.toFixed(0)})`,
								);
								// Select the new nodes
								if (newIds.length > 0) {
									updateViewState(
										viewState.applyMultiSelection(
											getViewState(),
											newIds,
											'replace',
										),
									);
								}
							})
							.catch((err) => {
								log?.('duplicate-error', String(err));
							});
					} else {
						// Legacy duplication (sync)
						const newIds = duplicateNodes(state.drag.nodes, offset);
						log?.(
							'duplicate',
							`${newIds.length} nodes at offset (${offset.x.toFixed(0)}, ${offset.y.toFixed(0)})`,
						);
					}
				} else {
					// Move nodes BEFORE clearing drag state so positions are updated
					// before render occurs with drag = null
					moveNodes(state.drag.nodes, offset);
					log?.(
						'drag-end',
						`offset: (${offset.x.toFixed(0)}, ${offset.y.toFixed(0)})`,
					);
				}
			}

			// Finish drag state after positions are updated
			updateViewState(viewState.finishDrag(state));
			input.popMode('drag');
		}),
	);

	// =========================================================================
	// Cancel Drag
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'escape',
			{
				mode: 'drag',
				command: { id: 'drag.cancel', name: 'Cancel Drag' },
			},
			() => {
				log?.('drag-cancel', '');
				updateViewState(viewState.cancelDrag(getViewState()));
				input.popMode('drag');
			},
		),
	);

	// =========================================================================
	// Cleanup
	// =========================================================================

	return () => {
		for (const unsub of unsubscribers) {
			unsub();
		}
	};
}
